import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { Map, MapPin, Trash2, Plus } from 'lucide-react';
import { AssignZoneModal } from './AssignZoneModal';

interface DriverZone {
  id: string;
  delivery_zone_id: string;
  name: string;
  code: string;
}

interface Props {
  driverProfileId: string;
}

export function DriverZonesList({ driverProfileId }: Props) {
  const { get, delete: del } = useApi();
  const { toast } = useToast();

  const [zones, setZones] = useState<DriverZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await get(`/api/v1/drivers/${driverProfileId}/zones`);
      const data = Array.isArray(res) ? res : res?.data || [];
      setZones(data);
    } catch {
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, [driverProfileId]);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const handleRemove = async (zoneId: string) => {
    setRemovingId(zoneId);
    try {
      await del(`/api/v1/drivers/${driverProfileId}/zones/${zoneId}`);
      toast({ title: 'تم إزالة المنطقة' });
      fetchZones();
    } catch {
      toast({ title: 'خطأ', description: 'فشل في إزالة المنطقة', variant: 'destructive' });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Map className="h-5 w-5" />
            مناطق التوصيل المعينة
          </CardTitle>
          <Button size="sm" className="gap-1" onClick={() => setShowAssignModal(true)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">تعيين منطقة</span>
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
            </div>
          ) : zones.length === 0 ? (
            <EmptyState
              icon={<MapPin className="h-10 w-10" />}
              title="لا توجد مناطق معينة"
              description="لم يتم تعيين هذا السائق لأي منطقة توصيل بعد"
            />
          ) : (
            <div className="space-y-2">
              {zones.map((zone) => (
                <div
                  key={zone.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{zone.name || zone.delivery_zone_id}</p>
                      {zone.code && (
                        <Badge variant="outline" className="text-[10px] mt-0.5">{zone.code}</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={removingId === zone.delivery_zone_id}
                    onClick={() => handleRemove(zone.delivery_zone_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AssignZoneModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSuccess={() => { fetchZones(); setShowAssignModal(false); }}
        driverProfileId={driverProfileId}
        assignedZoneIds={zones.map((z) => z.delivery_zone_id)}
      />
    </>
  );
}
